// Tool display constants for the prompt builder UI (36 tools)

export const TOOL_COLORS: Record<string, string> = {
  // Core LLMs
  ChatGPT: "border-emerald-500/40 bg-emerald-500/5",
  Claude: "border-orange-500/40 bg-orange-500/5",
  Gemini: "border-blue-500/40 bg-blue-500/5",
  Perplexity: "border-cyan-500/40 bg-cyan-500/5",
  NotebookLM: "border-yellow-500/40 bg-yellow-500/5",
  // Open-Source LLMs
  "DeepSeek V4": "border-teal-500/40 bg-teal-500/5",
  "Llama 4": "border-indigo-500/40 bg-indigo-500/5",
  "Grok": "border-slate-500/40 bg-slate-500/5",
  "Mistral Large 3": "border-amber-500/40 bg-amber-500/5",
  "Mistral Medium 3.5": "border-amber-400/40 bg-amber-400/5",
  "Qwen 3.6": "border-teal-400/40 bg-teal-400/5",
  // Code Tools
  "Claude Code": "border-orange-400/40 bg-orange-400/5",
  Cursor: "border-violet-500/40 bg-violet-500/5",
  "OpenAI Codex": "border-emerald-400/40 bg-emerald-400/5",
  // Image Generation
  "GPT Image 2": "border-green-500/40 bg-green-500/5",
  "Midjourney v8.1": "border-fuchsia-500/40 bg-fuchsia-500/5",
  "Ideogram 4.0": "border-pink-500/40 bg-pink-500/5",
  "FLUX 2 Max": "border-rose-500/40 bg-rose-500/5",
  // Video Generation
  "Seedance 2.0": "border-lime-500/40 bg-lime-500/5",
  "Veo 3.1": "border-sky-500/40 bg-sky-500/5",
  Synthesia: "border-purple-500/40 bg-purple-500/5",
  "Runway Gen-4.5": "border-red-500/40 bg-red-500/5",
  "Kling 3.0": "border-amber-400/40 bg-amber-400/5",
  // Design & Creative
  "Canva AI": "border-blue-400/40 bg-blue-400/5",
  "Adobe Firefly": "border-red-400/40 bg-red-400/5",
  // Sales & CRM
  "Salesforce Einstein": "border-sky-600/40 bg-sky-600/5",
  "HubSpot AI": "border-orange-600/40 bg-orange-600/5",
  "Apollo.io": "border-violet-400/40 bg-violet-400/5",
  // Email & Campaign
  Klaviyo: "border-green-400/40 bg-green-400/5",
  "Instantly.ai": "border-zinc-500/40 bg-zinc-500/5",
  // SEO & Content
  "Surfer SEO": "border-yellow-400/40 bg-yellow-400/5",
  Clearscope: "border-emerald-600/40 bg-emerald-600/5",
  // Social Media
  "Sprout Social": "border-green-600/40 bg-green-600/5",
  // Meeting & Productivity
  Granola: "border-stone-500/40 bg-stone-500/5",
  // Project Management
  "Asana AI": "border-rose-400/40 bg-rose-400/5",
  // Automation
  Zapier: "border-orange-500/40 bg-orange-500/5",
};

export type ToolCategoryKey =
  | "LLM"
  | "Code Tool"
  | "Image Generation"
  | "Video Generation"
  | "Design & Creative"
  | "Sales & CRM"
  | "Email & Campaign"
  | "SEO & Content"
  | "Social Media"
  | "Meeting & Productivity"
  | "Project Management"
  | "Automation";

export const TOOL_CATEGORY_MAP: Record<string, ToolCategoryKey> = {
  ChatGPT: "LLM",
  Claude: "LLM",
  Gemini: "LLM",
  Perplexity: "LLM",
  NotebookLM: "LLM",
  "DeepSeek V4": "LLM",
  "Llama 4": "LLM",
  "Grok": "LLM",
  "Mistral Large 3": "LLM",
  "Mistral Medium 3.5": "LLM",
  "Qwen 3.6": "LLM",
  "Claude Code": "Code Tool",
  Cursor: "Code Tool",
  "OpenAI Codex": "Code Tool",
  "GPT Image 2": "Image Generation",
  "Midjourney v8.1": "Image Generation",
  "Ideogram 4.0": "Image Generation",
  "FLUX 2 Max": "Image Generation",
  "Seedance 2.0": "Video Generation",
  "Veo 3.1": "Video Generation",
  Synthesia: "Video Generation",
  "Runway Gen-4.5": "Video Generation",
  "Kling 3.0": "Video Generation",
  "Canva AI": "Design & Creative",
  "Adobe Firefly": "Design & Creative",
  "Salesforce Einstein": "Sales & CRM",
  "HubSpot AI": "Sales & CRM",
  "Apollo.io": "Sales & CRM",
  Klaviyo: "Email & Campaign",
  "Instantly.ai": "Email & Campaign",
  "Surfer SEO": "SEO & Content",
  Clearscope: "SEO & Content",
  "Sprout Social": "Social Media",
  Granola: "Meeting & Productivity",
  "Asana AI": "Project Management",
  Zapier: "Automation",
};

export const CATEGORY_LABELS: Record<ToolCategoryKey, string> = {
  LLM: "AI Assistant",
  "Code Tool": "Code",
  "Image Generation": "Image Gen",
  "Video Generation": "Video Gen",
  "Design & Creative": "Design",
  "Sales & CRM": "Sales & CRM",
  "Email & Campaign": "Email",
  "SEO & Content": "SEO",
  "Social Media": "Social",
  "Meeting & Productivity": "Meetings",
  "Project Management": "PM",
  Automation: "Automation",
};

export const CATEGORY_BADGE_COLORS: Record<ToolCategoryKey, string> = {
  LLM: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300",
  "Code Tool": "bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300",
  "Image Generation": "bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-300",
  "Video Generation": "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300",
  "Design & Creative": "bg-fuchsia-100 text-fuchsia-700 dark:bg-fuchsia-900/30 dark:text-fuchsia-300",
  "Sales & CRM": "bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300",
  "Email & Campaign": "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300",
  "SEO & Content": "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300",
  "Social Media": "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300",
  "Meeting & Productivity": "bg-stone-100 text-stone-700 dark:bg-stone-900/30 dark:text-stone-300",
  "Project Management": "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300",
  Automation: "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300",
};

export const TOOLS_BY_CATEGORY: Record<string, string[]> = {
  "AI Assistants": ["Claude", "ChatGPT", "Gemini", "Perplexity", "NotebookLM", "DeepSeek V4", "Llama 4", "Grok", "Mistral Large 3", "Mistral Medium 3.5", "Qwen 3.6"],
  "Code": ["Claude Code", "Cursor", "OpenAI Codex"],
  "Image Generation": ["GPT Image 2", "Midjourney v8.1", "Ideogram 4.0", "FLUX 2 Max"],
  "Video Generation": ["Seedance 2.0", "Veo 3.1", "Synthesia", "Runway Gen-4.5", "Kling 3.0"],
  "Design & Creative": ["Canva AI", "Adobe Firefly"],
  "Sales & CRM": ["Salesforce Einstein", "HubSpot AI", "Apollo.io"],
  "Email & Campaign": ["Klaviyo", "Instantly.ai"],
  "SEO & Content": ["Surfer SEO", "Clearscope"],
  "Social Media": ["Sprout Social"],
  "Meetings": ["Granola"],
  "Project Management": ["Asana AI"],
  "Automation": ["Zapier"],
};
